const MOBILE_BREAKPOINT_QUERY = '(max-width: 699px)';
const SWIPE_MIN_DISTANCE = 40;
const SWIPE_CLICK_SUPPRESS_MS = 350;

class CoverSlideshow extends HTMLElement {
  connectedCallback() {
    if (!this._initialized) {
      this._initialize();
    }

    if (!this.slides.length) return;

    this._bindEvents();
    this._syncAria();
    this._setProgressState('paused');

    if (this._canAutoplay()) {
      this._startAutoplay();
    }
  }

  disconnectedCallback() {
    this._unbindEvents();
    this._stopAutoplay();
  }

  get cells() {
    return this.slides || [];
  }

  select(index) {
    if (!this.slides || this.slides.length === 0) return;

    const total = this.slides.length;
    const nextIndex = ((index % total) + total) % total;
    if (nextIndex === this.selectedIndex) return;

    const previousSlide = this.slides[this.selectedIndex];
    const nextSlide = this.slides[nextIndex];

    if (previousSlide) {
      previousSlide.classList.remove('is-selected');
      previousSlide.setAttribute('aria-hidden', 'true');
    }

    nextSlide.classList.add('is-selected');
    nextSlide.setAttribute('aria-hidden', 'false');

    this.selectedIndex = nextIndex;
    this.selectedCell = nextSlide;

    this.dispatchEvent(new CustomEvent('carousel:change', { detail: { index: nextIndex, cell: nextSlide } }));
    this.dispatchEvent(new CustomEvent('carousel:select', { detail: { index: nextIndex, cell: nextSlide } }));

    if (this.autoplayEnabled && !this.prefersReducedMotion) {
      this._restartAutoplay();
    }
  }

  next() {
    this.select(this.selectedIndex + 1);
  }

  previous() {
    this.select(this.selectedIndex - 1);
  }

  pause() {
    this._pausedByUser = true;
    this._stopAutoplay();
    this._setProgressState('paused');
  }

  resume() {
    if (!this._canAutoplay() || this._pausedByVisibility) return;
    this._pausedByUser = false;
    this._startAutoplay();
  }

  _startAutoplay() {
    this._stopAutoplay();
    this._setProgressState('running');
    this._interval = window.setInterval(() => this.next(), this.cycleSpeed * 1000);
    this._restartProgress();
  }

  _stopAutoplay() {
    if (this._interval) {
      window.clearInterval(this._interval);
      this._interval = null;
    }
  }

  _restartAutoplay() {
    if (this._pausedByUser || this._pausedByVisibility) return;
    this._startAutoplay();
  }

  _setProgressState(state) {
    this.style.setProperty('--slideshow-progress-duration', `${this.cycleSpeed}s`);
    this.style.setProperty('--slideshow-progress-play-state', state);
  }

  _restartProgress() {
    this.style.setProperty('--slideshow-progress-duration', '0s');
    this.style.setProperty('--slideshow-progress-play-state', 'paused');
    window.requestAnimationFrame(() => {
      this.style.setProperty('--slideshow-progress-duration', `${this.cycleSpeed}s`);
      this.style.setProperty('--slideshow-progress-play-state', 'running');
    });
  }

  _syncAria() {
    this.slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index === this.selectedIndex ? 'false' : 'true');
    });
  }

  _onVisibilityChange() {
    if (document.hidden) {
      this._pausedByVisibility = true;
      this._stopAutoplay();
      this._setProgressState('paused');
    } else {
      this._pausedByVisibility = false;
      if (this._canAutoplay() && !this._pausedByUser) {
        this._startAutoplay();
      }
    }
  }

  _onTouchStart(event) {
    if (!this._canSwipe(event)) return;

    const touch = event.changedTouches[0];
    this._touchStartX = touch.clientX;
    this._touchStartY = touch.clientY;
  }

  _onTouchEnd(event) {
    if (this._touchStartX === null || this._touchStartY === null) return;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this._touchStartX;
    const deltaY = touch.clientY - this._touchStartY;

    this._resetTouchState();

    if (Math.abs(deltaX) < SWIPE_MIN_DISTANCE || Math.abs(deltaX) <= Math.abs(deltaY)) return;

    if (deltaX < 0) {
      this.next();
    } else {
      this.previous();
    }

    this._suppressClickUntil = Date.now() + SWIPE_CLICK_SUPPRESS_MS;
  }

  _onTouchCancel() {
    this._resetTouchState();
  }

  _onClickCapture(event) {
    if (Date.now() >= this._suppressClickUntil) return;

    event.preventDefault();
    event.stopPropagation();
  }

  _canSwipe(event) {
    return this._mobileQuery.matches && this.slides.length > 1 && event.touches.length === 1;
  }

  _resetTouchState() {
    this._touchStartX = null;
    this._touchStartY = null;
  }

  _initialize() {
    this._initialized = true;
    this._eventsBound = false;
    this._pausedByUser = false;
    this._pausedByVisibility = false;

    this.slides = Array.from(this.querySelectorAll('.cover-slideshow__slide'));
    this.selectedIndex = this.slides.findIndex((slide) => slide.classList.contains('is-selected'));

    if (this.selectedIndex < 0 && this.slides.length > 0) {
      this.selectedIndex = 0;
      this.slides[0].classList.add('is-selected');
    }

    this.selectedCell = this.slides[this.selectedIndex] || null;
    this.autoplayEnabled = this.hasAttribute('autoplay');
    this.cycleSpeed = Number(this.dataset.cycleSpeed) || 6;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._mobileQuery = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
    this._touchStartX = null;
    this._touchStartY = null;
    this._suppressClickUntil = 0;
    this._viewport = this.querySelector('.cover-slideshow__viewport') || this;

    this._onVisibilityChange = this._onVisibilityChange.bind(this);
    this._onPointerEnter = () => this.pause();
    this._onPointerLeave = () => this.resume();
    this._onFocusIn = () => this.pause();
    this._onFocusOut = () => this.resume();
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
    this._onTouchCancel = this._onTouchCancel.bind(this);
    this._onClickCapture = this._onClickCapture.bind(this);
  }

  _bindEvents() {
    if (this._eventsBound) return;
    this._eventsBound = true;

    this.addEventListener('mouseenter', this._onPointerEnter);
    this.addEventListener('mouseleave', this._onPointerLeave);
    this.addEventListener('focusin', this._onFocusIn);
    this.addEventListener('focusout', this._onFocusOut);

    if (this._viewport) {
      this._viewport.addEventListener('touchstart', this._onTouchStart, { passive: true });
      this._viewport.addEventListener('touchend', this._onTouchEnd);
      this._viewport.addEventListener('touchcancel', this._onTouchCancel);
      this._viewport.addEventListener('click', this._onClickCapture, true);
    }

    document.addEventListener('visibilitychange', this._onVisibilityChange);
  }

  _unbindEvents() {
    if (!this._eventsBound) return;
    this._eventsBound = false;

    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    this.removeEventListener('mouseenter', this._onPointerEnter);
    this.removeEventListener('mouseleave', this._onPointerLeave);
    this.removeEventListener('focusin', this._onFocusIn);
    this.removeEventListener('focusout', this._onFocusOut);

    if (this._viewport) {
      this._viewport.removeEventListener('touchstart', this._onTouchStart);
      this._viewport.removeEventListener('touchend', this._onTouchEnd);
      this._viewport.removeEventListener('touchcancel', this._onTouchCancel);
      this._viewport.removeEventListener('click', this._onClickCapture, true);
    }
  }

  _canAutoplay() {
    return this.autoplayEnabled && !this.prefersReducedMotion;
  }
}

if (!window.customElements.get('cover-slideshow')) {
  window.customElements.define('cover-slideshow', CoverSlideshow);
}
